/**
 * @file AttachmentFileKeepToggle.tsx
 * @description The per-file "keep this on my computer" control. Consumer:
 * `AttachmentList`, once per file row.
 *
 * It subscribes to the pin map itself rather than taking pin state from the
 * list, so a pin write re-renders the toggles and not the 300 rows around them.
 * `useDesktopPins` refcounts one listener on `users/{uid}`, so every row on the
 * panel shares it.
 *
 * The panel's `disabled` and `anyOperationInProgress` are deliberately not
 * wired in. A pin lives on your own user document, so a document you may only
 * read is still one you may keep, and an upload in flight has nothing to do
 * with it (section 3 of the plan; the same deviation the artifact-level control
 * makes). The icon is the hard drive the artifact-level `DesktopKeepControl`
 * uses, drawn from the icon set the rest of the row uses.
 *
 * **The scope is written as an updater, never as a value.** `keep` runs it
 * inside the write chain against the scope as it then stands, so toggling two
 * files before either round trip returns keeps both. A plain array built at
 * render time compiles and passes every other test while silently dropping the
 * first uid. Removing the last uid is left to route through `keep` too — the
 * write layer turns an empty `files` scope into an unpin — so there is one
 * write path and no special case here.
 *
 * **Both directions share one `Popconfirm`**, switched by `state.confirm` so the
 * shell is never gated on mount. Stopping always asks, unlike adding a file to a
 * pin that already exists: nothing is ever deleted from the disk, but a change
 * the agent has not yet uploaded is stranded by the stop and keeping the file
 * again does not send it. That caveat is unconditional because this app has no
 * view of the reader's disk and cannot know whether an edit is pending.
 */

import { FC, useCallback, useContext, useState } from 'react';
import { Button, Popconfirm, Tooltip, Typography } from 'antd';
import { HddOutlined } from '@ant-design/icons';

import type { DesktopPinScope } from '#types';
import { MessageContext } from '../../../providers/MessageContext';
import { UserContext } from '../../../providers/UserContext';
import { keep } from '../../../use/desktopSync/keep';
import { useDesktopPin } from '../../../use/desktopSync/useDesktopPin';
import { resolveAttachmentKeepToggle } from './resolveAttachmentKeepToggle';

const { Text } = Typography;

const keepDescription = 'Syncretic File Sync copies this file to your computer and keeps it up to date. It uses space on your disk.';

const stopDescription = 'This file stays on your computer. If you changed it and Syncretic has not received that change yet, it will not be sent — and keeping the file again does not send it.';

interface AttachmentFileKeepToggleProps {
    /** The attachment this control keeps or stops keeping. */
    fileUid: string;
    /** The row's display name, used for the control's unique label. */
    fileName: string;
    /** Already folded by `resolveDesktopPinPath`; `null` renders disabled, not hidden. */
    pinPath: string | null;
}

export const AttachmentFileKeepToggle: FC<AttachmentFileKeepToggleProps> = ({
    fileUid,
    fileName,
    pinPath,
}) => {
    const { user } = useContext(UserContext);
    const { handleFirestoreWriteError } = useContext(MessageContext);
    const { scope, loading } = useDesktopPin(pinPath);
    const [busy, setBusy] = useState(false);

    const state = resolveAttachmentKeepToggle({
        fileName,
        fileUid,
        pinPath,
        signedIn: user?.uid !== undefined,
        loading,
        busy,
        scope,
    });

    const stopping = state.confirm === 'stop';

    const toggle = useCallback(async () => {
        const userId = user?.uid;
        if (!userId || !pinPath) return;

        setBusy(true);
        try {
            await keep(userId, pinPath, (current): DesktopPinScope => {
                if (current?.kind === 'document') return current;
                const fileUids = current?.kind === 'files' ? current.fileUids : [];
                return {
                    kind: 'files',
                    fileUids: fileUids.includes(fileUid)
                        ? fileUids.filter((keptFileUid) => keptFileUid !== fileUid)
                        : [...fileUids, fileUid],
                };
            });
        } catch (error) {
            handleFirestoreWriteError(error, '[AttachmentFileKeepToggle] keep', {
                userMessage: 'Could not change what you keep on your computer.',
            });
        } finally {
            setBusy(false);
        }
    }, [user?.uid, pinPath, fileUid, handleFirestoreWriteError]);

    return (
        <Tooltip title={state.tooltip}>
            <span style={{ display: 'inline-flex' }}>
                <Popconfirm
                    title={stopping
                        ? 'Stop keeping this file on your computer'
                        : 'Keep this file on your computer'}
                    description={<Text>{stopping ? stopDescription : keepDescription}</Text>}
                    okText={stopping ? 'Stop keeping' : 'Keep'}
                    okButtonProps={{ danger: stopping }}
                    cancelText="Cancel"
                    disabled={state.confirm === 'none'}
                    onConfirm={() => void toggle()}
                >
                    <Button
                        size="small"
                        icon={<HddOutlined />}
                        color={state.kept ? 'primary' : 'default'}
                        variant={state.kept ? 'filled' : 'text'}
                        disabled={state.disabled}
                        onClick={() => {
                            if (state.confirm === 'none') void toggle();
                        }}
                        id={`attachment-keep-toggle-${fileUid}`}
                        aria-label={state.ariaLabel}
                    />
                </Popconfirm>
            </span>
        </Tooltip>
    );
};
